// src/pages/User/FavoritesPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Heart, Play, Filter } from 'lucide-react';
import { contentService } from '../../services/contentService';
import type { ContentItem } from '../../types';
import MainLayout from '../../components/MainLayout/MainLayout';
import OriginalsContentCard from '../../components/Common/ContentCard';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import SearchFilter from '../../components/Common/SearchFilter';
import Carousel from '../../components/Common/Carousel';
import CarouselItem from '../../components/Common/CarouselItem';
import { uiActions } from '../../store/reducers/uiReducers';

const FavoritesPage: React.FC = () => {
  const dispatch = useDispatch();
  const [favorites, setFavorites] = useState<ContentItem[]>([]);
  const [filteredFavorites, setFilteredFavorites] = useState<ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');  
  const [searchTerm, setSearchTerm] = useState('');

  const fetchFavorites = useCallback(async () => {
    try {
      setIsLoading(true);
      const favoritesData = await contentService.getUserFavorites();
      setFavorites(favoritesData);
      setFilteredFavorites(favoritesData);
    } catch (error: unknown) {
      dispatch(uiActions.addNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load favorites'
      }));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Filter, search, and sort effect - runs when dependencies change
  useEffect(() => {
    let filtered = selectedType === 'all' ? favorites : favorites.filter(item => item.type === selectedType);
    
    if (searchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.tags && Array.isArray(item.tags) && item.tags.some(tag => tag?.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }
    
    // Apply sorting
    filtered = sortContent(filtered, sortBy);
    
    setFilteredFavorites(filtered);
  }, [favorites, selectedType, searchTerm, sortBy]);

  const handleTypeFilter = (type: string) => {
    setSelectedType(type);
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
  };

  // Group favorites by content type for carousels
  const getGroupedFavorites = useCallback(() => {
    if (filteredFavorites.length === 0) return {};

    const groups: { [key: string]: ContentItem[] } = {};
    
    // Group by content type
    filteredFavorites.forEach(item => {
      const type = item.type || 'content';
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(item);
    });

    // Only return groups with content
    return Object.fromEntries(
      Object.entries(groups).filter(([_, items]) => items.length > 0)
    );
  }, [filteredFavorites]);

  const sortContent = (content: ContentItem[], sortType: string) => {
    return [...content].sort((a, b) => {
      switch (sortType) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'most-liked':
          return b.likes - a.likes;
        case 'most-viewed':
          return b.views - a.views;
        case 'alphabetical':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });
  };

  const _handleUnfavorite = async (contentType: string, contentId: string) => {
    try {
      await contentService.favoriteContent(contentType, contentId);
      // Remove from local state
      const updated = favorites.filter(item => item.id !== contentId);
      setFavorites(updated);
      setFilteredFavorites(updated.filter(item => 
        selectedType === 'all' || item.type === selectedType
      ));
      
      dispatch(uiActions.addNotification({
        type: 'success',
        message: 'Removed from favorites'
      }));
    } catch (error: unknown) {
      dispatch(uiActions.addNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to remove from favorites'
      }));
    }
  };

  const contentTypes = [
    { key: 'all', label: 'All Favorites', icon: Heart },
    { key: 'cinema', label: 'cinema', icon: Play },
    { key: 'content', label: 'Content', icon: Play },
    { key: 'podcast', label: 'Podcasts', icon: Play },
    { key: 'animation', label: 'Animations', icon: Play },
    { key: 'sneak-peek', label: 'Sneak Peeks', icon: Play },
    { key: 'live-video', label: 'Live Videos', icon: Play },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'most-liked', label: 'Most Liked' },
    { value: 'most-viewed', label: 'Most Viewed' },
    { value: 'alphabetical', label: 'A-Z' },
  ];

  if (isLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center mb-4">
                  <Heart className="h-8 w-8 mr-3" />
                  <h1 className="text-4xl font-bold">My Favorites</h1>
                </div>
                <p className="text-xl text-pink-100">
                  Content you've loved ({favorites.length} items)
                </p>
              </div>
              

            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <SearchFilter
              onSearch={setSearchTerm}
              placeholder="Search your favorites..."
            />
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              {/* Type Filters */}
              <div className="flex flex-wrap gap-2">
                {contentTypes.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => handleTypeFilter(key)}
                    className={`flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedType === key
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-pink-500 focus:border-pink-500"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Content Display - Carousels by Type */}
          {Object.keys(getGroupedFavorites()).length > 0 ? (
            <div className="space-y-8">
              {Object.entries(getGroupedFavorites()).map(([type, items]) => {
                const typeLabels: { [key: string]: string } = {
                  cinema: 'Favorite Cinema',
                  content: 'Favorite Content',
                  podcast: 'Favorite Podcasts',
                  animation: 'Favorite Animations',
                  'sneak-peek': 'Favorite Sneak Peeks',
                  'live-video': 'Favorite Live Videos',
                };

                return (
                  <div key={type}>
                    <Carousel title={typeLabels[type] || `Favorite ${type}`} id={`favorites-${type}`}>
                      {items.map((item) => (
                        <CarouselItem key={item.id}>
                          <OriginalsContentCard
                            id={item.id}
                            title={item.title}
                            description={item.description}
                            thumbnail={item.thumbnail}
                            duration={item.duration}
                            type={item.type}
                            tags={item.tags}
                            views={item.views}
                            likes={item.likes}
                          />
                        </CarouselItem>
                      ))}
                    </Carousel>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Heart className="mx-auto h-16 w-16 text-gray-400 mb-6" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                {selectedType === 'all' ? 'No favorites yet' : `No ${selectedType} favorites found`}
              </h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">
                {selectedType === 'all' 
                  ? 'Start exploring content and heart the ones you love to see them here'
                  : `You haven't favorited any ${selectedType} content yet`
                }
              </p>
              <a
                href="/"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-pink-700 transition-colors"
              >
                <Heart className="h-5 w-5 mr-2" />
                Discover Content
              </a>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default FavoritesPage;
